import {useTheme} from "@mui/material";
import type {GridValidRowModel} from "@mui/x-data-grid-pro";
import {GridColDef} from "@mui/x-data-grid-pro";
import {useFormat} from "@sinch/core/hooks/format";
import * as React from "react";
import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {RATE_CELL_CLASS} from "@sinch/components/application/RatesCompositionTable/CalculationDataGrid";
import {CurrencyCell} from "@sinch/components/application/RatesCompositionTable/CurrencyCell";
import {
  TOTAL_ROW_ID
} from "@sinch/components/application/RatesCompositionTable/EntityOverviewTable/hooks/useTreeQueries";
import {PercentageCell} from "@sinch/components/application/RatesCompositionTable/PercentageCell";

/**
 * Share of the price that stays as margin. Undefined without a price to compare the margin against.
 *
 * @param price - Total price of the row
 * @param margin - Price minus wage of the row
 * @returns Ratio to be rendered as a percentage, `null` when it cannot be calculated
 */
const getMarginRatio = (price: number, margin: number): number | null => (price === 0 ? 0 : margin / price);

/**
 * Rate columns shared by the entity overview table and the rate component breakdown.
 */
export const useCommonRatesColumns = <
  TRow extends GridValidRowModel & { price: number; wage: number; total: number } = any,
>(): GridColDef<TRow>[] => {
  const { t } = useTranslation(["common", "draft"]);
  const { num } = useFormat();
  const { palette } = useTheme();

  return useMemo<GridColDef<TRow>[]>(
    () => [
      {
        field: "price",
        cellClassName: RATE_CELL_CLASS,
        headerName: t("price"),
        type: "number",
        align: "right",
        headerAlign: "right",
        display: "flex",
        width: 80,
        valueFormatter: (value) => num.precisionCurrency(Number(value ?? 0)),
        renderCell: ({ value, row }) => <CurrencyCell isTotalRow={row.id === TOTAL_ROW_ID} value={value ?? 0} />,
      },
      {
        field: "wage",
        cellClassName: RATE_CELL_CLASS,
        headerName: t("wage"),
        type: "number",
        align: "right",
        headerAlign: "right",
        display: "flex",
        width: 80,
        valueFormatter: (value) => num.precisionCurrency(Number(value ?? 0)),
        renderCell: ({ value, row }) => <CurrencyCell isTotalRow={row.id === TOTAL_ROW_ID} value={value ?? 0} />,
      },
      {
        field: "total",
        cellClassName: RATE_CELL_CLASS,
        headerName: t("profit"),
        type: "number",
        align: "right",
        headerAlign: "right",
        display: "flex",
        width: 80,
        valueFormatter: (value) => num.precisionCurrency(Number(value ?? 0)),
        renderCell: ({ value, row }) => (
          <CurrencyCell
            defaultPositiveColor={palette.success.main}
            isTotalRow={row.id === TOTAL_ROW_ID}
            value={Number(value)}
          />
        ),
      },
      {
        field: "marginPercentage",
        cellClassName: RATE_CELL_CLASS,
        headerName: t("profit") + " %",
        type: "number",
        align: "right",
        headerAlign: "right",
        display: "flex",
        width: 40,
        valueGetter: (_value, row) => getMarginRatio(row.price, row.total),
        valueFormatter: (value: number | null) =>
          value === null ? "" : num.percent(value, { maximumFractionDigits: 1 }),
        renderCell: ({ value, row }) => <PercentageCell isTotalRow={row.id === TOTAL_ROW_ID} value={value} />,
      },
    ],
    [t, num, palette.success.main]
  );
};
